import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useStoreCart } from "./StoreCartContext";
import { useStoreAuth } from "./StoreAuthContext";
import { CartCheckout, StoreSignIn } from "./components";
import CheckoutAddressModal from "./components/CheckoutAddressModal";
import { formatSavedAddressForOrder } from "./utils/checkoutAddressFormat";
import "./Store.css";
import { pixelInitiateCheckout, pixelPurchase } from "../../utils/fbPixel";

export default function Checkout() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation("global");
  const checkoutDir = (i18n.language || "").toLowerCase().startsWith("ar")
    ? "rtl"
    : "ltr";
  const checkoutLang = checkoutDir === "rtl" ? "ar" : "en";
  const { cart, cartTotal, updateQuantity, setCart } = useStoreCart();
  const {
    user,
    token,
    isAuthenticated,
    loading: authLoading,
    refreshUser,
  } = useStoreAuth();
  const [submitting, setSubmitting] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    note: "",
    paymentMethod: "online",
    latitude: "",
    longitude: "",
  });
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState(null);
  const [addAddressModalOpen, setAddAddressModalOpen] = useState(false);
  const [guestAddressModalOpen, setGuestAddressModalOpen] = useState(false);
  /** In-memory only: reopen guest address modal with same fields; cleared on full page refresh. */
  const [guestAddressSnapshot, setGuestAddressSnapshot] = useState(null);

  const savedAddresses = useMemo(
    () => user?.savedAddresses || [],
    [user?.savedAddresses]
  );

  /** Merge latest `name` / `arName` from API so checkout shows Arabic titles after old localStorage carts. */
  useEffect(() => {
    if (cart.length > 0) {
      pixelInitiateCheckout(cart, {
        email: user?.email,
        phone: user?.addresses?.[0]?.phone,
        externalId: user?._id,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cart.length === 0) return;
    let cancelled = false;
    axios
      .get("/store/products")
      .then(({ data }) => {
        if (cancelled || !Array.isArray(data)) return;
        const byId = new Map(data.map((p) => [String(p._id), p]));
        setCart((prev) => {
          let changed = false;
          const next = prev.map((item) => {
            const fresh = byId.get(String(item._id));
            if (!fresh) return item;
            const name = fresh.name ?? item.name;
            const arName = fresh.arName ?? item.arName;
            const freshImage = fresh.images?.[0] || fresh.image;
            const imageUrl = freshImage?.url || item.image?.url || "";
            if (
              name === item.name &&
              arName === item.arName &&
              imageUrl === (item.image?.url || "")
            )
              return item;
            changed = true;
            return { ...item, name, arName, image: freshImage || item.image };
          });
          return changed ? next : prev;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cart.length, setCart]);

  useEffect(() => {
    if (!user) return;
    setCheckoutForm((prev) => ({
      ...prev,
      fullName: user.fullName || prev.fullName,
      email: user.email || prev.email,
    }));
  }, [user]);

  useEffect(() => {
    if (!user?._id) return;
    if (savedAddresses.length === 0) {
      setSelectedSavedAddressId(null);
      localStorage.removeItem(`alramlaa_checkout_addr_${user._id}`);
      return;
    }
    const key = `alramlaa_checkout_addr_${user._id}`;
    setSelectedSavedAddressId((current) => {
      const stored = localStorage.getItem(key);
      const pick =
        current && savedAddresses.some((a) => String(a._id) === String(current))
          ? String(current)
          : stored &&
            savedAddresses.some((a) => String(a._id) === stored)
          ? stored
          : String(savedAddresses[0]._id);
      localStorage.setItem(key, pick);
      return pick;
    });
  }, [user?._id, savedAddresses]);

  useEffect(() => {
    if (!selectedSavedAddressId || !savedAddresses.length) return;
    const addr = savedAddresses.find(
      (a) => String(a._id) === String(selectedSavedAddressId)
    );
    if (!addr) return;
    const addrEmail = addr.email && String(addr.email).trim();
    setCheckoutForm((prev) => ({
      ...prev,
      fullName: addr.fullName,
      phone: addr.phone,
      email: addrEmail || user?.email || prev.email,
      address: formatSavedAddressForOrder(addr),
      latitude:
        addr.latitude != null && Number.isFinite(Number(addr.latitude))
          ? String(addr.latitude)
          : "",
      longitude:
        addr.longitude != null && Number.isFinite(Number(addr.longitude))
          ? String(addr.longitude)
          : "",
    }));
  }, [selectedSavedAddressId, savedAddresses, user?.email]);

  const handleCheckoutFormChange = useCallback((field, value) => {
    setCheckoutForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSelectSavedAddress = useCallback(
    (id) => {
      const sid = String(id);
      setSelectedSavedAddressId(sid);
      if (user?._id) {
        localStorage.setItem(`alramlaa_checkout_addr_${user._id}`, sid);
      }
    },
    [user?._id]
  );

  const handleDeleteSavedAddress = useCallback(
    async (addressId) => {
      if (!window.confirm("Remove this saved address?")) return;
      if (!token) return;
      try {
        await axios.delete(`/auth/addresses/${addressId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await refreshUser();
        toast.success(t("store.addressRemoved", { defaultValue: "Address removed." }));
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            t("store.couldNotDeleteAddress", { defaultValue: "Could not delete that address." })
        );
      }
    },
    [token, refreshUser]
  );

  const handleAddressSaved = useCallback(
    async (newAddr) => {
      await refreshUser();
      if (newAddr?._id && user?._id) {
        const id = String(newAddr._id);
        setSelectedSavedAddressId(id);
        localStorage.setItem(`alramlaa_checkout_addr_${user._id}`, id);
      }
    },
    [refreshUser, user?._id]
  );

  const handleGuestAddressApply = useCallback((payload) => {
    if (!payload) return;
    const { fullName, phone, email, address, latitude, longitude, snapshot } =
      payload;
    setCheckoutForm((prev) => ({
      ...prev,
      fullName,
      phone,
      email: email || prev.email,
      address,
      latitude: latitude || "",
      longitude: longitude || "",
    }));
    setGuestAddressSnapshot(snapshot || null);
  }, []);

  const deliveryReady = useMemo(() => {
    if (isAuthenticated) {
      if (!savedAddresses.length) return false;
      return (
        !!selectedSavedAddressId &&
        savedAddresses.some(
          (a) => String(a._id) === String(selectedSavedAddressId)
        ) &&
        !!checkoutForm.fullName?.trim() &&
        !!checkoutForm.phone?.trim() &&
        !!checkoutForm.address?.trim()
      );
    }

    // Guest checkout: require manual contact + address.
    return (
      !!checkoutForm.fullName?.trim() &&
      !!checkoutForm.phone?.trim() &&
      !!checkoutForm.address?.trim()
    );
  }, [
    savedAddresses,
    selectedSavedAddressId,
    checkoutForm.fullName,
    checkoutForm.phone,
    checkoutForm.address,
    isAuthenticated,
  ]);

  const submitOrder = useCallback(
    async (event) => {
      event.preventDefault();
      if (cart.length === 0) {
        toast.error(t("store.addProductsFirst", { defaultValue: "Add products to cart first." }));
        return;
      }
      if (!deliveryReady) {
        toast.error(t("store.chooseAddress", { defaultValue: "Choose or add a delivery address." }));
        return;
      }
      if (
        !checkoutForm.fullName ||
        !checkoutForm.phone ||
        !checkoutForm.address
      ) {
        toast.error(t("store.completeDeliveryDetails", { defaultValue: "Please complete delivery details." }));
        return;
      }
      try {
        setSubmitting(true);
        const latParsed = Number.parseFloat(String(checkoutForm.latitude || ""));
        const lngParsed = Number.parseFloat(String(checkoutForm.longitude || ""));
        const hasMapPin =
          String(checkoutForm.latitude || "").trim() &&
          String(checkoutForm.longitude || "").trim();
        const hasValidMapCoords =
          hasMapPin &&
          Number.isFinite(latParsed) &&
          Number.isFinite(lngParsed);
        const addressWithMap = hasValidMapCoords
          ? `${String(checkoutForm.address).trim()}\n📍 Map: ${latParsed}, ${lngParsed}`
          : checkoutForm.address;

        const payload = {
          customer: {
            fullName: checkoutForm.fullName,
            phone: checkoutForm.phone,
            email: checkoutForm.email,
            address: addressWithMap,
            note: checkoutForm.note,
            ...(hasValidMapCoords
              ? { latitude: latParsed, longitude: lngParsed }
              : {}),
          },
          paymentMethod: checkoutForm.paymentMethod,
          locale: checkoutLang,
          items: cart.map((item) => ({
            productId: item._id,
            quantity: item.quantity,
            selectedColor: item.selectedColor || undefined,
            selectedColorHex: item.selectedColorHex || undefined,
            // Curtain size options — the server recomputes the configured
            // price from these (cart's price_override is display-only).
            selectedWidth: item.selectedWidth ?? undefined,
            selectedHeight: item.selectedHeight ?? undefined,
            widthAdjustment: item.widthAdjustment || undefined,
            heightAdjustment: item.heightAdjustment || undefined,
            chainType: item.chainType || undefined,
          })),
        };
        if (user?._id) payload.userId = user._id;
        const response = await axios.post("/orders", payload);
        if (checkoutForm.paymentMethod === "online") {
          if (response?.data?.checkoutUrl) {
            // Clear the cart before leaving the app; the state persist effect
            // may not flush before the redirect, so clear localStorage too.
            setCart([]);
            try {
              localStorage.setItem("alramlaa_cart", "[]");
            } catch (_) {}
            window.location.assign(response.data.checkoutUrl);
            return;
          }
          toast.info(
            response?.data?.message ||
              t("store.onlinePaymentUnavailable", {
                defaultValue:
                  "Online payment is temporarily unavailable. Please try again or choose cash on delivery.",
              })
          );
        } else {
          toast.success(t("store.orderPlacedSuccess", { defaultValue: "Order placed successfully." }));
          // Only fire Purchase for COD, which is confirmed immediately. Online
          // payments fire it server-side after Tap confirms the charge.
          pixelPurchase(response?.data?.order);
        }
        const orderId = response?.data?.order?._id;
        setCart([]);
        setCheckoutForm({
          fullName: "",
          phone: "",
          email: "",
          address: "",
          note: "",
          paymentMethod: "online",
          latitude: "",
          longitude: "",
        });
        if (orderId && checkoutForm.paymentMethod !== "online") {
          navigate(`/store/my-orders/${orderId}`, { state: { orderPlaced: true } });
        } else {
          navigate("/store");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            t("store.failedToPlaceOrder", { defaultValue: "Failed to place order." })
        );
      } finally {
        setSubmitting(false);
      }
    },
    [cart, checkoutForm, deliveryReady, setCart, navigate, user, checkoutLang]
  );

  return (
    <section
      className="store-page store-checkout-page"
      dir={checkoutDir}
      lang={checkoutLang}
    >
      {isAuthenticated ? (
        <CheckoutAddressModal
          isOpen={addAddressModalOpen}
          onClose={() => setAddAddressModalOpen(false)}
          onSaved={(newAddr) => handleAddressSaved(newAddr)}
          defaultFullName={user?.fullName || ""}
          defaultPhone=""
          defaultEmail={user?.email || ""}
          token={token}
        />
      ) : (
        <CheckoutAddressModal
          guestMode
          isOpen={guestAddressModalOpen}
          onClose={() => setGuestAddressModalOpen(false)}
          onSaved={() => {}}
          onGuestApply={handleGuestAddressApply}
          guestInitialSnapshot={guestAddressSnapshot}
          defaultFullName={checkoutForm.fullName}
          defaultPhone={checkoutForm.phone}
          defaultEmail={checkoutForm.email}
          token={null}
        />
      )}
      <div className="container">
        <div className="store-checkout-page-inner">
          <Link to="/store" className="store-checkout-back">
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>{t("store.backToStore", { defaultValue: "Back to store" })}</span>
          </Link>

          {!authLoading && !isAuthenticated ? (
            <div className="store-checkout-auth-card">
              <div className="store-checkout-auth-card-copy">
                <h2 className="store-checkout-auth-title">
                  {t("store.checkout.authTitle", {
                    defaultValue: "Sign in to save your details (optional)",
                  })}
                </h2>
                <p className="store-checkout-auth-text">
                  {t("store.checkout.authText", {
                    defaultValue: "You can also place the order as a guest below.",
                  })}
                </p>
              </div>
              <div className="store-checkout-auth-actions">
                <StoreSignIn />
              </div>
            </div>
          ) : null}

          <CartCheckout
            cart={cart}
            cartTotal={cartTotal}
            checkoutForm={checkoutForm}
            submitting={submitting}
            deliveryReady={deliveryReady}
            savedAddresses={isAuthenticated ? savedAddresses : []}
            selectedSavedAddressId={selectedSavedAddressId}
            onSelectSavedAddress={handleSelectSavedAddress}
            onDeleteSavedAddress={handleDeleteSavedAddress}
            onOpenAddAddressModal={() => {
              if (!isAuthenticated) return;
              setAddAddressModalOpen(true);
            }}
            onOpenGuestAddressModal={() => setGuestAddressModalOpen(true)}
            canAddMoreAddresses={isAuthenticated ? savedAddresses.length < 5 : false}
            onUpdateQuantity={updateQuantity}
            onFormChange={handleCheckoutFormChange}
            onSubmit={submitOrder}
            firstName={user?.fullName?.trim().split(/\s+/)[0] || ""}
            allowGuestCheckout={!authLoading && !isAuthenticated}
          />
        </div>
      </div>
    </section>
  );
}
